// src/apis/outlookService.ts

import axios from "axios";

import axiosServices from "../utils/axios";

import type {
  EmailListItem,
  EmailDetails,
  Attachment,
  OutlookFolder,
} from "../utils/types/email";

interface ApiResponse<T> {
  data: T;
}

export const fetchEmailsList = async (params?: {
  folderId?: string;
  top?: number;
  filter?: string;
  $skip?: number;
  orderby?: string;
}): Promise<EmailListItem[]> => {
  const requestParams = { top: 25, ...params };
  const response = await axiosServices.get<ApiResponse<EmailListItem[]>>(
    "/outlook",
    { params: requestParams }
  );
  return response.data.data;
};

export const fetchEmailDetailsById = async (
  id: string
): Promise<EmailDetails> => {
  const response = await axiosServices.get<ApiResponse<EmailDetails>>(
    `/outlook/${id}`
  );
  return response.data.data;
};

export const downloadAttachment = async (
  emailId: string,
  attachmentId: string
): Promise<Blob> => {
  const response = await axiosServices.get(
    `/outlook/${emailId}/attachments/${attachmentId}`,
    { params: { format: "binary" }, responseType: "blob" }
  );
  return response.data;
};

export const updateEmail = async (
  id: string,
  data: Partial<EmailDetails>
): Promise<EmailDetails> => {
  const response = await axiosServices.patch<ApiResponse<EmailDetails>>(
    `/outlook/${id}`,
    data
  );
  return response.data.data;
};

export const linkAttachmentToJob = async (
  jobId: string,
  docTypeId: string,
  emailId: string,
  attachment: Attachment
): Promise<{ success: boolean }> => {
  try {
    const blob = await downloadAttachment(emailId, attachment.id);
    const signedUrlResponse = await axiosServices.get("/signed_url", {
      params: {
        ContentType: attachment.contentType,
        key: attachment.name,
        operation: "put",
        jobId,
      },
    });
    const { pre_signed_url: signedUrl, final_key: fileKey } =
      signedUrlResponse.data.data;
    await axios.put(signedUrl, blob, {
      headers: { "Content-Type": attachment.contentType },
    });
    await axiosServices.post(`/jobs/${jobId}/documents`, {
      key: fileKey,
      mime_type: attachment.contentType,
      size: attachment.size,
      type_id: docTypeId,
    });
    return { success: true };
  } catch (error) {
    console.error("Failed to link attachment:", error);
    throw error;
  }
};

export const moveEmail = async (
  id: string,
  folderId: string
): Promise<{ success: boolean }> => {
  const response = await axiosServices.post<ApiResponse<{ success: boolean }>>(
    `/outlook/${id}/move/${folderId}`
  );
  return response.data.data;
};

export const deleteEmail = async (id: string): Promise<{ success: boolean }> => {
  const response = await axiosServices.delete<ApiResponse<{ success: boolean }>>(
    `/outlook/${id}`
  );
  return response.data.data;
};

export const fetchFolders = async (): Promise<OutlookFolder[]> => {
  const response = await axiosServices.get<ApiResponse<OutlookFolder[]>>(
    "/outlook/folders"
  );
  return response.data.data;
};

export const searchEmails = async (params: {
  q: string;
  top?: number;
}): Promise<EmailListItem[]> => {
  const response = await axiosServices.get<ApiResponse<EmailListItem[]>>(
    "/outlook/search",
    { params: { q: params.q, top: params.top ?? 25 } }
  );
  return response.data.data;
};

export const sendEmail = async (data: {
  subject: string;
  body: { content: string; contentType: "HTML" | "Text" };
  toRecipients: { address: string; name?: string }[];
  ccRecipients?: { address: string; name?: string }[];
  attachments?: { name: string; contentType: string; contentBytes: string }[];
}): Promise<{ success: boolean }> => {
  const response = await axiosServices.post<ApiResponse<{ success: boolean }>>(
    `/outlook`,
    data
  );
  return response.data.data;
};

export const replyToEmail = async (
  id: string,
  message: string
): Promise<{ success: boolean }> => {
  const response = await axiosServices.post<ApiResponse<{ success: boolean }>>(
    `/outlook/${id}/reply`,
    { message }
  );
  return response.data.data;
};

export const addEmailTag = async (
  messageId: string,
  value: string
): Promise<{ categories: string[] }> => {
  const response = await axiosServices.post<ApiResponse<{ categories: string[] }>>(
    `/outlook/${messageId}/tag`,
    { value }
  );
  return response.data.data;
};

export const removeEmailTag = async (
  messageId: string,
  value: string
): Promise<{ categories: string[] }> => {
  const encodedValue = encodeURIComponent(value);
  const response = await axiosServices.delete<ApiResponse<{ categories: string[] }>>(
    `/outlook/${messageId}/tag?value=${encodedValue}`
  );
  return response.data.data;
};
